import React, { useState, useEffect, useCallback } from 'react';
import { ticketsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Ticket, Buildings, WarningCircle, CheckCircle, Clock, Warning,
  ArrowLeft, ChatCircle, Lock, CaretRight, Gauge, PhoneCall, Plus,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const priorityConfig = {
  critical: { label: 'Critical', cls: 'bg-red-100 text-red-700 border-red-200' },
  high: { label: 'High', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  medium: { label: 'Medium', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { label: 'Low', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const statusConfig = {
  open: { label: 'Open', cls: 'bg-blue-100 text-blue-700' },
  assigned: { label: 'Assigned', cls: 'bg-indigo-100 text-indigo-700' },
  in_progress: { label: 'In Progress', cls: 'bg-violet-100 text-violet-700' },
  waiting_for_user: { label: 'Waiting for User', cls: 'bg-amber-100 text-amber-700' },
  waiting_for_payer: { label: 'Waiting for Payer', cls: 'bg-orange-100 text-orange-700' },
  escalated: { label: 'Escalated', cls: 'bg-red-100 text-red-700' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', cls: 'bg-slate-100 text-slate-600' },
  reopened: { label: 'Reopened', cls: 'bg-rose-100 text-rose-700' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const TicketAdminPage = () => {
  const [view, setView] = useState('overview'); // overview | hospital | detail
  const [overviewData, setOverviewData] = useState(null);
  const [hospitalId, setHospitalId] = useState('');
  const [hospitalName, setHospitalName] = useState('');
  const [tickets, setTickets] = useState([]);
  const [ticketTotal, setTicketTotal] = useState(0);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resolutionText, setResolutionText] = useState('');
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });

  // Raise Ticket state
  const [raiseDialogOpen, setRaiseDialogOpen] = useState(false);
  const [raiseForm, setRaiseForm] = useState({
    tenant_id: '', category: '', priority: 'medium', subject: '', description: '',
    caller_name: '', caller_contact: '',
  });
  const [raising, setRaising] = useState(false);
  const [hospitalsList, setHospitalsList] = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);

  const TICKET_CATEGORIES = [
    "Claim Issue", "Pre-auth Issue", "Document Pending", "Query from TPA/Insurance",
    "Denial / Rejection Issue", "Payment / Settlement Issue", "User Access Issue",
    "Login Issue", "Configuration Request", "API / Integration Issue",
    "Technical Bug", "Report / Dashboard Issue", "Other"
  ];

  useEffect(() => { loadOverview(); }, []);

  const loadOverview = async () => {
    setLoading(true);
    try { const res = await ticketsApi.adminOverview(); setOverviewData(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openHospital = async (hId, hName) => {
    setHospitalId(hId);
    setHospitalName(hName);
    setView('hospital');
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.search) params.search = filters.search;
      const res = await ticketsApi.adminHospitalTickets(hId, params);
      setTickets(res.data.tickets || []);
      setTicketTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const reloadHospitalTickets = useCallback(() => {
    if (hospitalId) openHospital(hospitalId, hospitalName);
  }, [hospitalId, hospitalName, filters]);

  const openTicketDetail = async (ticketOid) => {
    setLoading(true);
    try {
      const res = await ticketsApi.adminGetTicket(ticketOid);
      setTicket(res.data);
      setResolutionText(res.data.resolution_summary || '');
      setView('detail');
      // Load hospital users for assignment
      if (res.data.tenant_id) {
        try {
          const usersRes = await ticketsApi.adminGetHospitalUsers(res.data.tenant_id);
          setAssignUsers(usersRes.data || []);
        } catch { setAssignUsers([]); }
      }
    } catch (e) { toast.error('Failed to load ticket'); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'resolved' && resolutionText) payload.resolution_summary = resolutionText;
      await ticketsApi.adminUpdateTicket(ticket.id, payload);
      toast.success(`Status changed to ${statusConfig[newStatus]?.label || newStatus}`);
      openTicketDetail(ticket.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setUpdating(false); }
  };

  const handlePriorityChange = async (priority) => {
    setUpdating(true);
    try {
      await ticketsApi.adminUpdateTicket(ticket.id, { priority });
      toast.success('Priority updated');
      openTicketDetail(ticket.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setUpdating(false); }
  };

  const handleAssign = async (userId) => {
    setUpdating(true);
    try {
      await ticketsApi.adminUpdateTicket(ticket.id, { assigned_to: userId || '' });
      toast.success(userId ? 'Ticket assigned' : 'Ticket unassigned');
      openTicketDetail(ticket.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
    finally { setUpdating(false); }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setCommenting(true);
    try {
      await ticketsApi.adminAddComment(ticket.id, { content: commentText, is_internal: isInternal });
      toast.success('Comment added');
      setCommentText('');
      openTicketDetail(ticket.id);
    } catch (e) { toast.error('Failed'); }
    finally { setCommenting(false); }
  };

  const goBack = () => {
    if (view === 'detail') { setView('hospital'); reloadHospitalTickets(); }
    else if (view === 'hospital') { setView('overview'); loadOverview(); }
  };

  const openRaiseDialog = async (preselectedHospitalId = '') => {
    // Load hospitals list for dropdown
    try {
      const res = await ticketsApi.adminOverview();
      const hospitals = (res.data?.hospitals || []).map(h => ({ id: h.hospital_id, name: h.hospital_name }));
      setHospitalsList(hospitals);
    } catch (e) { console.error(e); }
    setRaiseForm({
      tenant_id: preselectedHospitalId || hospitalId || '',
      category: '', priority: 'medium', subject: '', description: '',
      caller_name: '', caller_contact: '',
    });
    setRaiseDialogOpen(true);
  };

  const handleRaiseTicket = async () => {
    if (!raiseForm.tenant_id || !raiseForm.category || !raiseForm.subject) {
      toast.error('Hospital, Category and Subject are required');
      return;
    }
    setRaising(true);
    try {
      const res = await ticketsApi.adminRaiseTicket(raiseForm);
      toast.success(res.data?.message || 'Ticket raised successfully');
      setRaiseDialogOpen(false);
      // Refresh the current view
      if (view === 'overview') loadOverview();
      else if (view === 'hospital') reloadHospitalTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to raise ticket');
    } finally { setRaising(false); }
  };

  if (loading && !overviewData && !ticket) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>;

  // =================== OVERVIEW VIEW ===================
  if (view === 'overview') {
    const p = overviewData?.platform_totals || {};
    const hospitals = overviewData?.hospitals || [];
    return (
      <div className="space-y-5" data-testid="ticket-admin-page">
        <Toaster position="top-right" richColors />
        <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-violet-900 rounded-2xl p-6 text-white" data-testid="ticket-admin-header">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
              <Ticket className="h-6 w-6 text-indigo-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']">Platform Ticket Management</h1>
              <p className="text-sm text-slate-300 mt-0.5">Click a hospital to view & manage tickets</p>
            </div>
            <div className="ml-auto">
              <Button onClick={() => openRaiseDialog()} className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2 text-sm" data-testid="raise-ticket-btn">
                <PhoneCall className="h-4 w-4" /> Raise Ticket for Hospital
              </Button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: 'Total Tickets', value: p.total, icon: Ticket, color: 'text-slate-700', bg: 'bg-slate-50' },
            { label: 'Open', value: p.open, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Escalated', value: p.escalated, icon: WarningCircle, color: 'text-orange-600', bg: 'bg-orange-50' },
            { label: 'Overdue', value: p.overdue, icon: Warning, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Hospitals Active', value: p.hospitals_with_tickets, icon: Buildings, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          ].map((s, i) => (
            <Card key={i} className={`${s.bg} border-0 shadow-sm`}><CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{s.label}</p>
                  <p className={`text-2xl font-bold font-['Outfit'] mt-1 ${s.color}`}>{s.value}</p></div>
                <s.icon className={`h-6 w-6 ${s.color}`} weight="duotone" />
              </div>
            </CardContent></Card>
          ))}
        </div>
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="hospitals-tickets-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hospital</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Open</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Escalated</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Overdue</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">SLA %</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {hospitals.map(h => (
                  <TableRow key={h.hospital_id} className="hover:bg-indigo-50/30 cursor-pointer" onClick={() => openHospital(h.hospital_id, h.hospital_name)}>
                    <TableCell><p className="text-sm font-medium text-slate-800">{h.hospital_name}</p><p className="text-[10px] text-slate-400">{h.city}</p></TableCell>
                    <TableCell className="text-center"><span className="text-sm font-bold">{h.total}</span></TableCell>
                    <TableCell className="text-center">{h.open > 0 ? <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{h.open}</Badge> : <span className="text-xs text-slate-300">0</span>}</TableCell>
                    <TableCell className="text-center">{h.escalated > 0 ? <Badge className="bg-orange-100 text-orange-700 border-0 text-[10px]">{h.escalated}</Badge> : <span className="text-xs text-slate-300">0</span>}</TableCell>
                    <TableCell className="text-center">{h.overdue > 0 ? <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">{h.overdue}</Badge> : <span className="text-xs text-slate-300">0</span>}</TableCell>
                    <TableCell className="text-center"><Badge className={`border-0 text-[10px] ${h.sla_compliance_pct >= 90 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{h.sla_compliance_pct}%</Badge></TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm" className="text-indigo-600 text-xs gap-1"><CaretRight className="h-3.5 w-3.5" /> View</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <Warning className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-700">Patient identifiers are masked in ticket views. Platform team can respond and manage tickets without accessing PHI.</p>
        </div>
        <RaiseTicketDialog open={raiseDialogOpen} onClose={() => setRaiseDialogOpen(false)}
          form={raiseForm} setForm={setRaiseForm} hospitalsList={hospitalsList}
          categories={TICKET_CATEGORIES} raising={raising} onSubmit={handleRaiseTicket} />
      </div>
    );
  }

  // =================== HOSPITAL TICKETS LIST ===================
  if (view === 'hospital') {
    return (
      <div className="space-y-5" data-testid="hospital-tickets-view">
        <Toaster position="top-right" richColors />
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={goBack} className="text-slate-500"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div><h1 className="text-lg font-bold font-['Outfit'] text-slate-800">{hospitalName} - Tickets</h1>
            <p className="text-xs text-slate-500">{ticketTotal} tickets</p></div>
          <div className="ml-auto">
            <Button onClick={() => openRaiseDialog(hospitalId)} className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2 text-sm" data-testid="raise-ticket-hospital-btn">
              <PhoneCall className="h-4 w-4" /> Raise Ticket
            </Button>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Input placeholder="Search..." className="w-[200px] h-9 text-sm" value={filters.search}
            onChange={e => setFilters(f => ({...f, search: e.target.value}))} data-testid="admin-search" />
          <Select value={filters.status || 'all'} onValueChange={v => { setFilters(f => ({...f, status: v === 'all' ? '' : v})); }}>
            <SelectTrigger className="w-[140px] h-9 text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem>
              {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-9 bg-indigo-500 text-white text-xs" onClick={() => openHospital(hospitalId, hospitalName)}>Search</Button>
        </div>
        <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="p-0">
          {loading ? <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" /></div> :
          tickets.length === 0 ? <div className="text-center py-10 text-slate-400 text-sm">No tickets</div> :
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Ticket</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Subject</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Priority</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Status</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Raised By</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Created</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {tickets.map(t => (
                <TableRow key={t.id} className="hover:bg-indigo-50/30 cursor-pointer" onClick={() => openTicketDetail(t.id)}>
                  <TableCell><span className="text-xs font-mono font-semibold text-indigo-600">{t.ticket_id}</span></TableCell>
                  <TableCell><p className="text-sm text-slate-700 truncate max-w-[250px]">{t.subject}</p></TableCell>
                  <TableCell className="text-center"><Badge className={`${priorityConfig[t.priority]?.cls || ''} border text-[10px]`}>{priorityConfig[t.priority]?.label}</Badge></TableCell>
                  <TableCell className="text-center"><Badge className={`${statusConfig[t.status]?.cls || ''} border-0 text-[10px]`}>{statusConfig[t.status]?.label}</Badge></TableCell>
                  <TableCell><span className="text-xs text-slate-600">{t.raised_by_name}</span></TableCell>
                  <TableCell><span className="text-xs text-slate-500">{formatDate(t.created_at)}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>}
        </CardContent></Card>
        <RaiseTicketDialog open={raiseDialogOpen} onClose={() => setRaiseDialogOpen(false)}
          form={raiseForm} setForm={setRaiseForm} hospitalsList={hospitalsList}
          categories={TICKET_CATEGORIES} raising={raising} onSubmit={handleRaiseTicket} />
      </div>
    );
  }

  // =================== TICKET DETAIL VIEW ===================
  if (view === 'detail' && ticket) {
    const t = ticket;
    return (
      <div className="space-y-5" data-testid="admin-ticket-detail">
        <Toaster position="top-right" richColors />
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={goBack} className="text-slate-500"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono font-bold text-indigo-600">{t.ticket_id}</span>
              <Badge className="bg-indigo-50 text-indigo-600 border border-indigo-200 text-[10px]">{t.hospital_name}</Badge>
              <Badge className={`${priorityConfig[t.priority]?.cls || ''} border text-[10px]`}>{priorityConfig[t.priority]?.label}</Badge>
              <Badge className={`${statusConfig[t.status]?.cls || ''} border-0 text-[10px]`}>{statusConfig[t.status]?.label}</Badge>
              {t.is_overdue && <Badge className="bg-red-100 text-red-700 border-red-200 border text-[10px]">Overdue</Badge>}
            </div>
            <h1 className="text-lg font-bold font-['Outfit'] text-slate-800 mt-1">{t.subject}</h1>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">Description</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-slate-600 whitespace-pre-wrap">{t.description}</p></CardContent>
            </Card>
            {t.resolution_summary && (
              <Card className="bg-emerald-50 border border-emerald-200"><CardContent className="p-4">
                <p className="text-xs font-semibold text-emerald-800 mb-1">Resolution Summary</p>
                <p className="text-sm text-emerald-700">{t.resolution_summary}</p>
              </CardContent></Card>
            )}
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <ChatCircle className="h-4 w-4 text-indigo-500" /> Comments & Activity
              </CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[...(t.activities || []).map(a => ({...a, type: 'activity'})),
                  ...(t.comments || []).map(c => ({...c, type: 'comment'})),
                ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      item.type === 'comment' ? (item.is_internal ? 'bg-amber-100' : 'bg-indigo-100') : 'bg-slate-100'
                    }`}>{item.type === 'comment' ? (item.is_internal ? <Lock className="h-3.5 w-3.5 text-amber-600" /> : <ChatCircle className="h-3.5 w-3.5 text-indigo-600" />) : <CaretRight className="h-3.5 w-3.5 text-slate-500" />}</div>
                    <div className="flex-1 min-w-0">
                      {item.type === 'comment' ? (
                        <div className={`rounded-lg p-3 ${item.is_internal ? 'bg-amber-50 border border-amber-200' : item.user_name?.includes('Platform') ? 'bg-indigo-50 border border-indigo-200' : 'bg-slate-50 border border-slate-200'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-slate-700">{item.user_name}</span>
                            {item.is_internal && <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px]">Internal</Badge>}
                            {item.user_name?.includes('Platform') && <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[9px]">Platform</Badge>}
                            <span className="text-[10px] text-slate-400">{formatDate(item.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap">{item.content}</p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 py-1.5">
                          <span className="text-xs text-slate-500">{item.detail}</span>
                          <span className="text-[10px] text-slate-400">{formatDate(item.created_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {t.status !== 'closed' && (
                  <div className="pt-3 border-t border-slate-200 space-y-2">
                    <Textarea placeholder="Reply to hospital..." rows={3} value={commentText}
                      onChange={e => setCommentText(e.target.value)} className="border-slate-200 resize-none text-sm" data-testid="admin-comment-input" />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                        <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded border-slate-300" />
                        <Lock className="h-3 w-3" /> Internal note (hospital won't see)
                      </label>
                      <Button size="sm" disabled={commenting || !commentText.trim()} onClick={handleComment}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs gap-1" data-testid="admin-post-comment-btn">
                        {commenting ? 'Posting...' : 'Post Reply'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="space-y-4">
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">Actions</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {t.allowed_statuses?.length > 0 && (<div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Change Status</Label>
                  {t.allowed_statuses.includes('resolved') && (
                    <Textarea placeholder="Resolution summary..." rows={2} value={resolutionText}
                      onChange={e => setResolutionText(e.target.value)} className="border-slate-200 resize-none text-xs mt-1 mb-2" />
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {t.allowed_statuses.map(s => (
                      <Button key={s} variant="outline" size="sm" disabled={updating}
                        className={`text-[10px] h-7 ${statusConfig[s]?.cls || ''} border`}
                        onClick={() => handleStatusChange(s)} data-testid={`admin-status-btn-${s}`}>
                        {statusConfig[s]?.label || s}
                      </Button>
                    ))}
                  </div>
                </div>)}
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Priority</Label>
                  <Select value={t.priority} onValueChange={handlePriorityChange}>
                    <SelectTrigger className="border-slate-200 mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Assign To</Label>
                  <Select value={t.assigned_to || 'none'} onValueChange={v => handleAssign(v === 'none' ? '' : v)}>
                    <SelectTrigger className="border-slate-200 mt-1 h-8 text-xs" data-testid="admin-assign-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {assignUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.role?.replace(/_/g,' ')})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">Details</CardTitle></CardHeader>
              <CardContent className="space-y-2.5">
                {[
                  { label: 'Hospital', value: t.hospital_name },
                  { label: 'Category', value: t.category },
                  { label: 'Branch', value: t.branch_name || '-' },
                  { label: 'Raised By', value: t.raised_by_name },
                  { label: 'Assigned To', value: t.assigned_to_name || 'Unassigned' },
                  { label: 'Created', value: formatDate(t.created_at) },
                  { label: 'SLA Due', value: formatDate(t.sla_due), highlight: t.is_overdue },
                  { label: 'Claim Ref', value: t.related_claim_no || '-' },
                  { label: 'Patient', value: t.related_patient_name || '-' },
                  { label: 'Payer', value: t.related_payer_name || '-' },
                ].map((d, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{d.label}</span>
                    <span className={`text-xs font-medium ${d.highlight ? 'text-red-600' : 'text-slate-700'}`}>{d.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// =================== RAISE TICKET DIALOG (rendered outside view blocks) ===================
const RaiseTicketDialog = ({ open, onClose, form, setForm, hospitalsList, categories, raising, onSubmit }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-['Outfit'] flex items-center gap-2">
          <PhoneCall className="h-5 w-5 text-indigo-500" /> Raise Ticket for Hospital
        </DialogTitle>
        <DialogDescription>Create a support ticket on behalf of a hospital (e.g., from a phone call)</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label className="text-xs font-semibold">Hospital *</Label>
          <Select value={form.tenant_id} onValueChange={v => setForm(f => ({...f, tenant_id: v}))}>
            <SelectTrigger className="border-slate-200" data-testid="raise-hospital-select"><SelectValue placeholder="Select hospital..." /></SelectTrigger>
            <SelectContent>
              {hospitalsList.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold">Category *</Label>
            <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
              <SelectTrigger className="border-slate-200" data-testid="raise-category-select"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold">Priority *</Label>
            <Select value={form.priority} onValueChange={v => setForm(f => ({...f, priority: v}))}>
              <SelectTrigger className="border-slate-200" data-testid="raise-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold">Subject *</Label>
          <Input value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))}
            placeholder="Brief description of the issue" className="border-slate-200" data-testid="raise-subject" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Description</Label>
          <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
            rows={4} placeholder="Detailed description from the caller..." className="border-slate-200 resize-none text-sm" data-testid="raise-description" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold">Caller Name</Label>
            <Input value={form.caller_name} onChange={e => setForm(f => ({...f, caller_name: e.target.value}))}
              placeholder="Who called?" className="border-slate-200" data-testid="raise-caller-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Caller Contact</Label>
            <Input value={form.caller_contact} onChange={e => setForm(f => ({...f, caller_contact: e.target.value}))}
              placeholder="Phone / Email" className="border-slate-200" data-testid="raise-caller-contact" />
          </div>
        </div>
        <Button onClick={onSubmit} disabled={raising || !form.tenant_id || !form.category || !form.subject}
          className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 text-white gap-2"
          data-testid="raise-ticket-submit-btn">
          <PhoneCall className="h-4 w-4" /> {raising ? 'Raising...' : 'Raise Ticket & Notify Hospital'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Wrap the main component to include the dialog
const TicketAdminPageWrapper = () => {
  return <TicketAdminPage />;
};

export default TicketAdminPage;
